import { Waves, Radar, Network, Compass, ArrowUpRight } from "lucide-react";

const CAPABILITIES = [
  {
    n: "01",
    title: "Computational Modeling",
    icon: Waves,
    body: "Developing mathematical and numerical models that describe physical processes, dynamical systems, and data-generating mechanisms with precision and reproducibility.",
  },
  {
    n: "02",
    title: "Sensing and Inference",
    icon: Radar,
    body: "Designing methods to extract reliable information from noisy, indirect, or incomplete measurements — including statistical inference and signal-based estimation.",
  },
  {
    n: "03",
    title: "Optimization & Machine Learning",
    icon: Network,
    body: "Research and implementation of optimization, estimation, and learning techniques for structured problems where interpretability and robustness matter.",
  },
  {
    n: "04",
    title: "Intelligent Decision Support",
    icon: Compass,
    body: "Translating computational outputs into clear, decision-relevant information for complex operational and analytical contexts where human judgment remains central.",
  },
];

export default function ResearchFocus() {
  return (
    <section
      id="research"
      data-testid="research-section"
      className="relative py-24 md:py-36 border-t border-white/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-end justify-between mb-14 md:mb-20 gap-8 flex-wrap">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500">
                02 / Research Focus
              </span>
              <span className="w-24 h-px bg-white/10" />
            </div>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter font-medium text-white leading-[1.02] max-w-3xl">
              Capability areas that form our technical foundation.
            </h2>
          </div>
          <p className="text-sm md:text-base text-zinc-400 max-w-sm font-light leading-relaxed">
            Our research spans four interconnected areas. Each is pursued with
            mathematical rigor and designed to compose into broader systems.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 border border-white/10">
          {CAPABILITIES.map((c, i) => {
            const Icon = c.icon;
            const borderClasses = [
              i % 2 === 0 ? "md:border-r border-white/10" : "",
              i < 2 ? "md:border-b border-white/10" : "",
              i > 0 ? "border-t md:border-t-0 border-white/10" : "",
            ].join(" ");
            return (
              <article
                key={c.n}
                data-testid={`capability-card-${c.n}`}
                className={`group relative p-8 md:p-12 bg-[#0a0a0a] hover:bg-[#111111] transition-colors duration-300 ${borderClasses}`}
              >
                <div className="flex items-start justify-between mb-10">
                  <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-zinc-500">
                    {c.n}
                  </span>
                  <Icon
                    size={22}
                    strokeWidth={1.25}
                    className="text-zinc-400 group-hover:text-white transition-colors"
                  />
                </div>

                <h3 className="font-heading text-2xl md:text-3xl tracking-tight font-medium text-white mb-5 leading-tight">
                  {c.title}
                </h3>
                <p className="text-[15px] md:text-base text-zinc-400 leading-relaxed font-light max-w-md">
                  {c.body}
                </p>

                <div className="mt-10 flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-zinc-500 group-hover:text-white transition-colors">
                  <span>Capability</span>
                  <ArrowUpRight
                    size={12}
                    className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
